<!--
  feedbackOptOutField: opt-out of the automated QM feedback requests ("Feedback-Anfragen (QM)", field noFeedbackRequests)
  as a profile row with switch (same pattern as the "Frühwarnungs-Mails" row). For parents, students (customer), teachers
  (employees), Standorte (institutions) and Ansprechpartner (responsibles). The parent shows it only with read access on the
  field (v-can … keys ['noFeedbackRequests']); without update access the switch is disabled with a reason.
  The unsubscribe link of every request sets the same flag.
  Props: entity ({_id, noFeedbackRequests}), entityType
-->
<template>
  <profile-field :icon="optedOut ? 'mdi-message-off-outline' : 'mdi-message-reply-text-outline'" label="Feedback-Anfragen (QM)">
    <div class="fld__value">{{ optedOut ? 'abgemeldet' : 'werden verschickt' }}</div>
    <div class="fld__note">{{ hint }}</div>
    <template #actions>
      <span :class="{ 'app-tip-wrap': !canUpdate }" v-tip="canUpdate ? '' : 'Dir fehlt das Recht, diese Einstellung zu ändern.'">
        <v-switch :key="renderKey" :disabled="!canUpdate || saving" :input-value="!optedOut" :loading="saving" aria-label="Feedback-Anfragen verschicken"
                  class="mt-0 pt-0" color="info" dense hide-details inset @change="(send) => save(!send)"/>
      </span>
    </template>
  </profile-field>
</template>

<script lang="ts">
import Vue from 'vue';
import {Types} from 'mongoose';
import ProfileField from '@/components/common/profileUi/ProfileField.vue';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {CrudActionTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudActionTypes';
import {ParentHandlerApi} from '@/classes/api/parent-handler.api.class';
import {StudentHandlerApi} from '@/classes/api/student-handler.api.class';
import {EmployeeHandlerAPI} from '@/classes/api/employee-handler.api.class';
import {InstitutionerApi} from '@/classes/api/institutioner.api.class';
import {ResponsibleHandlerAPI} from '@/classes/api/responsible-handler.api.class';

const FIELD = 'noFeedbackRequests';

export type FeedbackOptOutEntityType = CrudEntityTypes.PARENT | CrudEntityTypes.STUDENT | CrudEntityTypes.EMPLOYEE | CrudEntityTypes.INSTITUTION
  | CrudEntityTypes.RESPONSIBLE;

const WHO: Record<string, string> = {
  [CrudEntityTypes.PARENT]: 'diese Familie',
  [CrudEntityTypes.STUDENT]: 'diese:n Kund:in',
  [CrudEntityTypes.EMPLOYEE]: 'diese Lehrkraft',
  [CrudEntityTypes.INSTITUTION]: 'den Standort (Sekretariat)',
  [CrudEntityTypes.RESPONSIBLE]: 'diese:n Ansprechpartner:in',
};

export default Vue.extend({
  components: {ProfileField},
  props: {
    entity: {type: Object as () => { _id: Types.ObjectId; noFeedbackRequests?: boolean }, required: true},
    entityType: {type: String as () => FeedbackOptOutEntityType, required: true},
  },
  data: () => ({
    saving: false,
    localValue: undefined as boolean | undefined,
    renderKey: 0,
  }),
  computed: {
    optedOut(): boolean {
      return this.localValue ?? !!this.entity.noFeedbackRequests;
    },
    hint(): string {
      const who = WHO[this.entityType] || 'diese Person';
      return this.optedOut
        ? `Keine automatischen Feedback-Anfragen (Mail / WhatsApp) an ${who}.`
        : `Nach Kontaktpunkten kann eine kurze Feedback-Anfrage an ${who} gehen (Abmeldung auch über den Link in jeder Anfrage).`;
    },
    canUpdate(): boolean {
      return this.$$crudAccessManager.canAccessEntity(this.entityType, CrudActionTypes.UPDATE, this.entity._id, FIELD);
    },
  },
  methods: {
    async update(value: boolean) {
      const body = {noFeedbackRequests: value};
      switch (this.entityType) {
        case CrudEntityTypes.PARENT:
          return ParentHandlerApi.update(this.entity._id, body);
        case CrudEntityTypes.STUDENT:
          return StudentHandlerApi.update(this.entity._id, body);
        case CrudEntityTypes.EMPLOYEE:
          return EmployeeHandlerAPI.update(this.entity._id, body);
        case CrudEntityTypes.INSTITUTION:
          return InstitutionerApi.update(this.entity._id, body);
        default:
          return ResponsibleHandlerAPI.update(this.entity._id, body);
      }
    },
    async save(value: boolean) {
      const next = !!value;
      this.saving = true;
      try {
        await this.update(next);
        this.localValue = next;
        this.$$showSnackbar(next ? 'Keine Feedback-Anfragen mehr' : 'Feedback-Anfragen wieder erlaubt', 'success');
      } catch (e) {
        // reset the switch to the saved value
        this.renderKey++;
        this.$$showSnackbar('Fehler beim Speichern der Einstellung', 'error', e);
      } finally {
        this.saving = false;
      }
    },
  },
  watch: {
    'entity.noFeedbackRequests'() {
      this.localValue = undefined;
    },
  },
});
</script>
